'use client';

import { useEffect, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useAppStore } from '@/store/app/appStore';
import styled from 'styled-components';
import { Loader2 } from 'lucide-react';
import * as apiAuth from '@/api/auth';
import { ROOT_AUTH_URL, ROOT_URL } from '@/constants/url';

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 16px;
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

// Public routes that don't require authentication
const PUBLIC_ROUTES = ['/login'];

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider - handles authentication only
 *
 * Flow:
 * 1. Check token via /api/v1/account/profile
 * 2. Success -> setProfile() -> render children
 * 3. Failure -> redirect to auth subdomain
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const pathname = usePathname();
  const { appState, setProfile } = useAppStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const initStartedRef = useRef(false);

  // Check if current route is public
  const isPublicRoute = PUBLIC_ROUTES.some(route => pathname.startsWith(route));

  // Initial authentication check
  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    const checkAuth = async () => {
      // Skip auth check for public routes
      if (isPublicRoute) {
        useAppStore.getState().setLoading(false);
        setIsInitialized(true);
        return;
      }

      try {
        const account = await apiAuth.account();
        setProfile(account);
        setIsInitialized(true);
      } catch {
        window.location.href = `${ROOT_AUTH_URL}/login?redirect_path=${ROOT_URL}&no_return=0`;
      } finally {
        useAppStore.getState().setLoading(false);
      }
    };

    checkAuth();
  }, [isPublicRoute, setProfile]);

  // Show loading state while checking authentication
  if (!isInitialized || appState.loading) {
    return (
      <LoadingWrapper>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <LoadingText>Загрузка...</LoadingText>
      </LoadingWrapper>
    );
  }

  return <>{children}</>;
}
